import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { addFlag, removeFlag } from '@databases/flags';
import { defaultStoreUtils } from '@databases/redux';
import { selectHasSelectedCells } from '@databases/redux/cellSelections/selectors';
import { useCollabManager } from '@databases/utils/hooks/useCollabManager';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../messages';
import { doProcessPastedContent } from '../redux/actions';

import { usePasteListener } from './usePaste';

export interface UseImportListenerProps {
  contentId: string;
  currentViewId: string;
  isDisabled?: boolean;
}

/**
 * Listens to document paste events and imports the pasted content into the database (if some cells are selected)
 * or opens the import dialog with the pasted content preselected.
 */
export const useImportListener = ({
  contentId,
  currentViewId,
  isDisabled,
}: UseImportListenerProps) => {
  const scope = 'main';
  const hasSelection = defaultStoreUtils.useEqualSelector((state) =>
    selectHasSelectedCells(state, { contentId })
  );

  const dispatch = defaultStoreUtils.useDispatch();
  const { formatMessage } = useIntl();

  const collabManager = useCollabManager(contentId);
  const undoManager = collabManager?.database.undoManager;
  const client = useAnalytics();

  const onPastingContent = useCallback(
    (flagId: string, messageId: keyof typeof messages) => {
      addFlag({
        id: flagId,
        type: 'warning',
        body: formatMessage(messages[messageId]),
        actions: [
          {
            content: formatMessage(
              messages['snackbar-warning.pasting-over-existing-content-undo']
            ),
            onClick: () => {
              undoManager?.undo();
              removeFlag(flagId);
            },
          },
        ],
        showIcon: true,
      });
    },
    [formatMessage, undoManager]
  );

  const handlePaste = useCallback(
    (files: DataTransferItemList | File[]) => {
      client.sendContentPastedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
      });

      const fieldLabel = formatMessage(
        messages['import-field-settings.field-name-inline-edit-default-text'],
        {
          defaultFieldName: formatMessage(messages['import-field-settings.type-text']),
        }
      );
      dispatch(
        doProcessPastedContent(
          contentId,
          files,
          scope,
          currentViewId,
          fieldLabel,
          onPastingContent
        )
      );
    },
    [client, contentId, formatMessage, dispatch, currentViewId, onPastingContent]
  );

  usePasteListener({ onPaste: handlePaste, isDisabled: !hasSelection || isDisabled });
};
